import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck, Loader2, UserPlus, Users, Baby, Cat, User } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  parent: { label: "Parent", icon: User },
  enfant: { label: "Enfant", icon: Baby },
  animal: { label: "Animal", icon: Cat },
  autre: { label: "Autre", icon: Users },
};
const PALETTE = ["#22293A", "#C2492A", "#1F6F5C", "#8B8577", "#E0663F", "#4E5262", "#6D7182", "#A93D22"];

export default function Members() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [family, setFamily] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [famOpen, setFamOpen] = useState(false);
  const [fam, setFam] = useState({ name: "", type: "enfant", color: PALETTE[2] });
  const [savingFam, setSavingFam] = useState(false);

  const load = () => {
    api.get("/members").then((r) => setUsers(r.data));
    api.get("/family-members").then((r) => setFamily(r.data));
  };
  useEffect(load, []);

  const createUser = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Nom, email et mot de passe (6+ caractères) requis"); return; }
    setSaving(true);
    try {
      await api.post("/members", form);
      toast.success("Compte ajouté");
      setOpen(false); setForm({ name: "", email: "", password: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  const removeUser = async (id) => { await api.delete(`/members/${id}`); load(); toast.success("Compte supprimé"); };

  const createFam = async () => {
    if (!fam.name.trim()) { toast.error("Nom requis"); return; }
    setSavingFam(true);
    try {
      await api.post("/family-members", fam);
      toast.success("Membre de la famille ajouté");
      setFamOpen(false); setFam({ name: "", type: "enfant", color: PALETTE[2] }); load();
    } catch { toast.error("Erreur"); } finally { setSavingFam(false); }
  };
  const removeFam = async (id) => { await api.delete(`/family-members/${id}`); load(); toast.success("Membre supprimé"); };

  return (
    <div className="space-y-8" data-testid="members-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Espace Couple & Famille</h1>
        <p className="text-muted-foreground text-sm mt-1">Gérez les accès à l'application et les membres de la famille suivis dans les dépenses.</p>
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Membres de la famille</h2>
          <Button onClick={() => setFamOpen(true)} data-testid="add-family-button"><Plus className="w-4 h-4 mr-1.5" />Ajouter</Button>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">Parents, enfants, animaux… Chaque dépense pourra être attribuée à un ou plusieurs d'entre eux.</p>
        {family.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted-foreground">Aucun membre de la famille pour l'instant.</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {family.map((m) => {
              const meta = TYPE_META[m.type] || TYPE_META.autre;
              const Icon = meta.icon;
              return (
                <Card key={m.id} className="p-4 flex items-center gap-3" data-testid={`family-card-${m.id}`}>
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0" style={{ backgroundColor: m.color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{m.name}</div>
                    <div className="text-xs text-muted-foreground">{meta.label}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeFam(m.id)} data-testid={`delete-family-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Comptes de connexion</h2>
          <Button variant="outline" onClick={() => setOpen(true)} data-testid="add-member-button"><UserPlus className="w-4 h-4 mr-1.5" />Ajouter un accès</Button>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">Seules ces personnes peuvent se connecter à l'application.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {users.map((m) => (
            <Card key={m.id} className="p-5 flex items-center gap-4" data-testid={`member-card-${m.id}`}>
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-semibold text-lg">
                {m.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{m.name}</span>
                  {m.role === "admin" && <Badge variant="secondary" className="gap-1 font-normal"><ShieldCheck className="w-3 h-3" />Propriétaire</Badge>}
                </div>
                <div className="text-sm text-muted-foreground truncate">{m.email}</div>
              </div>
              {m.role !== "admin" && m.id !== user?.id && (
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeUser(m.id)} data-testid={`delete-member-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </Card>
          ))}
        </div>
      </section>

      <Dialog open={famOpen} onOpenChange={setFamOpen}>
        <DialogContent data-testid="family-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouveau membre de la famille</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nom</Label><Input value={fam.name} onChange={(e) => setFam({ ...fam, name: e.target.value })} placeholder="Ex: Louis, Rex" data-testid="family-name-input" /></div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={fam.type} onValueChange={(v) => setFam({ ...fam, type: v })}>
                <SelectTrigger data-testid="family-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="enfant">Enfant</SelectItem>
                  <SelectItem value="animal">Animal</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Couleur</Label>
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((c) => (
                  <button key={c} type="button" onClick={() => setFam({ ...fam, color: c })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${fam.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }} data-testid={`family-color-${c}`} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFamOpen(false)}>Annuler</Button><Button onClick={createFam} disabled={savingFam} data-testid="save-family-button">{savingFam ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle className="font-display">Ajouter un accès</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Thérèse" data-testid="member-name-input" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="conjoint@exemple.com" data-testid="member-email-input" /></div>
            <div className="space-y-1.5"><Label>Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 6 caractères" data-testid="member-password-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={createUser} disabled={saving} data-testid="save-member-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
